import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Crown, Check, ShieldCheck, Lock, ExternalLink, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const STRIPE_LINK = process.env.REACT_APP_STRIPE_PAYMENT_LINK;

const perks = [
  'Unlimited Sage AI conversations',
  'Personalised family profiles',
  'Advanced safety cross-checking',
  'Smart shopping lists & reminders'
];

export default function Checkout() {
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-20 text-center min-h-[70vh]">
        <span className="w-14 h-14 rounded-2xl bg-botanica-greenLight flex items-center justify-center mx-auto mb-5">
          <Lock className="w-6 h-6 text-botanica-green" />
        </span>
        <h1 className="font-serif text-3xl text-botanica-ink mb-2">Sign in to continue</h1>
        <p className="text-botanica-muted mb-6">Please create an account or sign in before starting your Premium trial.</p>
        <button onClick={() => openAuth('register')} className="px-6 py-3 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors">
          Create free account
        </button>
      </div>
    );
  }

  const pay = () => {
    if (STRIPE_LINK) {
      window.open(STRIPE_LINK, '_blank', 'noopener');
      setStarted(true);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 py-12 min-h-[70vh]">
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-botanica-green bg-botanica-greenLight px-3 py-1 rounded-full mb-4">
          <Crown className="w-3 h-3" /> Premium checkout
        </span>
        <h1 className="font-serif text-4xl text-botanica-ink">Start your 7-day free trial</h1>
        <p className="text-botanica-muted mt-2">You won't be charged today. Cancel anytime.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        {/* Order summary */}
        <div className="bg-botanica-card/60 rounded-3xl p-7 border border-botanica-card">
          <h2 className="font-serif text-xl text-botanica-ink mb-5">Order summary</h2>
          <div className="flex items-center justify-between pb-4 border-b border-botanica-card">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-botanica-greenLight flex items-center justify-center">
                <Leaf className="w-5 h-5 text-botanica-green" />
              </span>
              <div>
                <p className="font-medium text-botanica-ink">Premium membership</p>
                <p className="text-xs text-botanica-muted">Billed monthly after trial</p>
              </div>
            </div>
            <span className="font-serif text-lg text-botanica-ink">$1.99</span>
          </div>
          <div className="flex items-center justify-between py-3 text-sm">
            <span className="text-botanica-muted">7-day free trial</span>
            <span className="text-botanica-green font-medium">−$1.99</span>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-botanica-card">
            <span className="font-medium text-botanica-ink">Due today</span>
            <span className="font-serif text-2xl text-botanica-ink">$0.00</span>
          </div>
          <ul className="mt-6 space-y-2.5">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-sm text-botanica-ink">
                <Check className="w-4 h-4 text-botanica-green mt-0.5 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>

        {/* Payment */}
        <div className="bg-botanica-panel rounded-3xl p-7 border border-botanica-card">
          <h2 className="font-serif text-xl text-botanica-ink mb-2">Payment</h2>
          <p className="text-sm text-botanica-muted mb-5 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-botanica-green" /> Securely processed by Stripe
          </p>

          <div className="rounded-2xl bg-botanica-card/60 p-4 mb-5 text-sm text-botanica-muted leading-relaxed">
            For your security, card details are entered on Stripe's encrypted payment page — we never see or store your card number.
          </div>

          {!STRIPE_LINK ? (
            <p className="text-sm text-botanica-warn">Payment link is not configured yet.</p>
          ) : !started ? (
            <button
              onClick={pay}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors"
            >
              <Lock className="w-4 h-4" /> Continue to secure payment
              <ExternalLink className="w-4 h-4" />
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-botanica-ink">A secure payment window opened in a new tab. Once your payment is complete, continue below.</p>
              <button
                onClick={() => navigate('/premium/success')}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors"
              >
                I've completed my payment <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={pay} className="w-full py-2.5 rounded-full text-sm text-botanica-muted hover:text-botanica-ink transition-colors">
                Re-open payment page
              </button>
            </div>
          )}

          <p className="text-[11px] text-botanica-muted mt-4 text-center">Signed in as {user.email}</p>
        </div>
      </div>
    </div>
  );
}
